import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, useTheme, useMediaQuery, Typography } from '@mui/material';
import { colors } from '../../../themes/typography';
import { responsiveSpacing } from '../../../themes/responsive';
import { experimentColors } from '../../../themes/experimentColors';
import ExperimentColumn from './ExperimentColumn';
import AlertColumn from './AlertColumn';

const ExperimentBoard = ({ 
  experiments, 
  onExperimentAction, 
  loading, 
  itemsPerPage = 20, 
  onDataMutation, 
  clientId, 
  summarized = false,
  isAlertsMode = false, // New prop to switch between experiments and alerts
  // Props for create experiment form
  currentFilters,
  filterOptions,
  onCreateSuccess,
  onCreateError,
  filtersRef
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Different columns for alerts vs experiments
  const columns = isAlertsMode ? [
    {
      id: 'pending',
      title: t('pending'),
      alerts: experiments.pending || [],
      color: experimentColors.pending,
      isAlertColumn: true
    },
    {
      id: 'under_review',
      title: t('underReview') || 'Under Review',
      alerts: experiments.under_review || [],
      color: experimentColors.running, // Orange for under review
      isAlertColumn: true
    },
    {
      id: 'resolved',
      title: t('resolved') || 'Resolved',
      alerts: experiments.resolved || [],
      color: experimentColors.accepted, // Blue for resolved
      isAlertColumn: true
    }
  ] : [
    {
      id: 'pending',
      title: t('pending'),
      experiments: experiments.pending || [],
      color: experimentColors.pending
    },
    {
      id: 'accepted',
      title: t('accepted'),
      experiments: experiments.accepted || [],
      color: experimentColors.accepted
    },
    {
      id: 'running',
      title: t('running'),
      experiments: experiments.running || [],
      color: experimentColors.running
    },
    {
      id: 'finished',
      title: t('finished'),
      experiments: experiments.finished || [],
      color: experimentColors.finished
    },
    {
      id: 'rejected',
      title: t('rejected'),
      experiments: experiments.rejected || [],
      color: experimentColors.rejected
    }
  ];

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? responsiveSpacing.md : isAlertsMode ? '16px' : '4px', // More generous gap for alerts
      minHeight: 'auto',
      maxHeight: 'none',
      overflowX: 'auto',
      overflowY: 'visible',
      p: isMobile ? responsiveSpacing.xs : isAlertsMode ? '16px' : '4px', // More padding for alerts
      backgroundColor: colors.background.default,
      borderRadius: '12px',
      border: `1px solid ${colors.border.light}`,
      width: '100%',
      maxWidth: isAlertsMode ? '986px' : 'none', // Increased by 10% from previous (896 * 1.1)
      margin: isAlertsMode ? '0 auto' : '0', // Center the container when in alerts mode
      boxSizing: 'border-box'
    }}>
        {columns.map((column) => {
          // Handle alert columns with new AlertColumn component
          if (column.isAlertColumn) {
            return (
              <AlertColumn
                key={column.id}
                column={column}
                onAlertAction={onExperimentAction}
                loading={loading}
                itemsPerPage={10} // 10 alerts per page per column
                clientId={clientId}
                onDataRefresh={onDataMutation}
                onOperationSuccess={onCreateSuccess}
                onOperationError={onCreateError}
              />
            );
          }

          return (
            <ExperimentColumn
              key={column.id}
              column={column}
              onExperimentAction={onExperimentAction}
              loading={loading}
              itemsPerPage={itemsPerPage}
              onDataMutation={onDataMutation}
              clientId={clientId}
              summarized={summarized}
              currentFilters={currentFilters}
              filterOptions={filterOptions}
              onCreateSuccess={onCreateSuccess}
              onCreateError={onCreateError}
            />
          );
        })}
    </Box>
  );
};

export default ExperimentBoard;
